/**
 * 온보딩 작업 등록 + 문서 생성 단계 컴포넌트 (진단 모달 STEP 3~5).
 *
 * 대상 사용자는 안전 지식이 없는 사업주 — 원칙:
 *  · 자유 입력 최소화: 업종 프리셋 체크박스가 기본, 서술·문서 업로드는 보조 입력
 *  · 2026-08-08 통합: 작업 선택과 현장 상세가 **한 화면** — 체크한 작업 카드를 펼치면
 *    그 작업의 상세(장소·장비·위험요인, 추천 미리 체크)가 인라인으로 붙는다.
 *    상세는 작업별(SetupTaskIn.detail)로 저장돼 생성 프롬프트에 그 작업 문맥만 들어간다.
 *  · 이름은 현장 말로 편집 유도(동명 작업 구분·검색성) — "2공장 자재창고 지게차 운반"
 *  · 생성은 서버 잡 — 화면을 닫아도 계속되고, 돌아오면 완성돼 있다
 *
 * 홈 '작업 추가'에서도 TasksStep 을 재사용한다(embedded — 라디오 없음, CTA는 부모).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  FiCheck, FiChevronDown, FiChevronUp, FiEdit3, FiFileText, FiGrid,
  FiLoader, FiPlus, FiUploadCloud, FiX, FiZap, FiAlertTriangle, FiArrowRight,
} from 'react-icons/fi'
import Button from '../../components/ui/Button'
import Spinner from '../../components/ui/Spinner'
import CheckboxGroupWithCustom from '../../components/common/CheckboxGroupWithCustom'
import DropdownWithCustom from '../../components/common/DropdownWithCustom'
import { toast } from '../../hooks/useToast'
import {
  EQUIPMENT_OPTIONS, HAZARD_CATEGORY_OPTIONS, LOCATION_OPTIONS,
  PPE_OPTIONS, SAFEGUARD_OPTIONS, WORK_CONDITION_OPTIONS, WORKER_COUNT_OPTIONS,
} from '../../data/formOptions'
import { sitesApi } from '../../lib/sitesApi'
import { mergeRecommendations } from '../../data/recommendations'
import { filterEquipmentForIndustry, presetsFor, workTypeRiskRank } from '../../data/taskPresets'
import { extractFileText } from '../../lib/extractFileText'
import { riskAssessmentApi, TIER_LABEL, type RAItem } from '../../lib/riskAssessmentApi'
import { onboardingApi, type SetupDetail, type SetupJobStatus, type SetupTaskIn } from './onboardingApi'

const MAX_TASKS = 15

/** 상시 근로자 수(진단 입력) → WORKER_COUNT_OPTIONS 버킷 프리필. */
export function workerBucket(n: number): string {
  if (n <= 1) return '1명 (단독)'
  if (n === 2) return '2명'
  if (n === 3) return '3명'
  if (n <= 5) return '4~5명'
  if (n <= 10) return '6~10명'
  if (n <= 20) return '11~20명'
  return '21명 이상'
}

/** 작업 1개의 추천(정적) — work_type 기반 + **업종 필터**(음식점에 지게차 추천 금지).
 * 추천이 업종에 없는 설비를 기본 체크하면 사용자가 그냥 넘어갈 때 그 장비가 '있다'고
 * 기록돼 업종 가드(생성 품질)가 무력화된다. 손으로 체크하는 것은 막지 않는다. */
function taskRec(t: SetupTaskIn, ksic?: string | null, location?: string | null) {
  const m = mergeRecommendations(t.work_type || undefined, location || undefined)
  return {
    conditions: [...new Set(m.conditions)],
    equipment: filterEquipmentForIndustry([...new Set(m.equipment)], ksic),
    hazards: [...new Set(m.hazards)], ppe: [...new Set(m.ppe)], safeguards: [...new Set(m.safeguards)],
  }
}

/** 상세 최초 펼침 시 시드 — 추천 미리 체크 + 인원 버킷 프리필("그냥 다음" 30초 통과).
 * site 문맥이 있으면 보유 보호구(대장)·MSDS 화학물질을 함께 미리 체크한다 —
 * 사업장에 이미 등록된 실물이 작업 문서에 그대로 반영되도록. */
export function seedTaskDetail(t: SetupTaskIn, workers: number, ksic?: string | null,
                               site?: { ppe?: string[]; hazards?: string[] }): SetupDetail {
  const rec = taskRec(t, ksic)
  return {
    worker_count: workers > 0 ? workerBucket(workers) : null,
    equipment: rec.equipment.slice(0, 6),
    conditions: rec.conditions.slice(0, 6),
    hazards: [...new Set([...(t.hazard_hints ?? []), ...(site?.hazards ?? []), ...rec.hazards])].slice(0, 10),
    safeguards: rec.safeguards.slice(0, 6),
    ppe: [...new Set([...(site?.ppe ?? []), ...rec.ppe])].slice(0, 10),
  }
}

/** 이름 인라인 편집 — 로컬 상태로 타이핑(공백 포함), blur/Enter 에 커밋.
 * 키 입력마다 부모 배열을 갱신하면 key 가 바뀌어 포커스가 날아간다(재마운트). */
function TaskNameInput({ name, onCommit }: { name: string; onCommit: (next: string) => void }) {
  const [draft, setDraft] = useState(name)
  useEffect(() => { setDraft(name) }, [name])
  return (
    <input value={draft} onChange={e => setDraft(e.target.value)}
      onBlur={() => { if (draft.trim() && draft.trim() !== name) onCommit(draft.trim()); else setDraft(name) }}
      onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
      className="min-w-0 flex-1 bg-transparent text-[12.5px] font-semibold text-ink focus:outline-none focus:ring-1 focus:ring-brand-primary/40 rounded px-1"
      title="우리 현장에서 부르는 이름으로 바꿔 주세요 (예: 2공장 자재창고 지게차 운반)" />
  )
}

/* ────────────────────────────────────────────────────────────────────────────
 * 작업 1개의 현장 상세 필드 (카드 펼침 안) — 추천 미리 체크 + 이름에 장소 붙이기
 * ──────────────────────────────────────────────────────────────────────────── */
function TaskDetailFields({ task, ksic, onChange, onRename, sitePpe = [], siteChemHazards = [] }: {
  task: SetupTaskIn
  ksic?: string | null
  onChange: (detail: SetupDetail) => void
  onRename?: (name: string) => void
  /** 사업장 대장에 등록된 보유 보호구 품명 — 옵션·추천에 '보유' 문맥으로 주입. */
  sitePpe?: string[]
  /** MSDS·유해인자 대장의 화학물질 → 위험요인 후보("화학물질(품명) 취급"). */
  siteChemHazards?: string[]
}) {
  const d = task.detail ?? {}
  const rec = useMemo(() => taskRec(task, ksic, d.location), [task.work_type, ksic, d.location]) // eslint-disable-line react-hooks/exhaustive-deps
  const set = (patch: Partial<SetupDetail>) => onChange({ ...d, ...patch })
  // 보유 보호구·MSDS 물질을 정적 옵션에 병합(중복 제거) + 추천 최상단 배치.
  const ppeOptions = useMemo(() => {
    const known = new Set(PPE_OPTIONS.map(o => o.value))
    return [...PPE_OPTIONS,
      ...sitePpe.filter(n => !known.has(n)).map(n => ({ value: n, label: `${n} (대장 보유)` }))]
  }, [sitePpe])
  const hazardOptions = useMemo(() => {
    const known = new Set(HAZARD_CATEGORY_OPTIONS.map(o => o.value))
    return [...HAZARD_CATEGORY_OPTIONS,
      ...siteChemHazards.filter(n => !known.has(n)).map(n => ({ value: n, label: `${n} — MSDS 등록` }))]
  }, [siteChemHazards])
  const ppeRec = useMemo(() => [...new Set([...sitePpe, ...rec.ppe])], [sitePpe, rec.ppe])
  const hazardRec = useMemo(() => [...new Set([...siteChemHazards, ...rec.hazards])], [siteChemHazards, rec.hazards])
  const canPrefixLocation =
    !!onRename && !!d.location && !task.name.includes(d.location)

  return (
    <div className="space-y-3 rounded-b-xl border-x border-b border-surface-border bg-surface-elevated/20 p-3">
      <p className="text-[11px] text-ink-faint leading-relaxed">
        ✨ <b className="text-ink-muted">이 작업</b> 기준으로 추천 항목을 미리 체크해 뒀어요 —
        다르면 고치고, 맞으면 그대로 두세요. 여기 적은 내용이 서류에 그대로 반영됩니다.
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <DropdownWithCustom label="이 작업을 하는 장소" options={LOCATION_OPTIONS}
            value={d.location ?? ''} onChange={v => set({ location: v || null })}
            placeholder="선택 (비워도 됩니다)" recommendedKeys={undefined} />
          {canPrefixLocation && (
            <button type="button" onClick={() => onRename!(`${d.location} ${task.name}`)}
              className="text-[10.5px] font-medium text-brand-primary hover:underline">
              작업 이름 앞에 ‘{d.location}’ 붙이기 →
            </button>
          )}
        </div>
        <DropdownWithCustom label="같이 일하는 인원" options={WORKER_COUNT_OPTIONS}
          value={d.worker_count ?? ''} onChange={v => set({ worker_count: v || null })}
          placeholder="선택" />
      </div>
      <CheckboxGroupWithCustom label="이 작업에 쓰는 장비·기계" options={EQUIPMENT_OPTIONS}
        value={d.equipment ?? []} onChange={v => set({ equipment: v })}
        recommendedKeys={rec.equipment} columns={3} customPlaceholder="기타 장비 직접 입력" />
      <CheckboxGroupWithCustom label="작업 환경·조건" options={WORK_CONDITION_OPTIONS}
        value={d.conditions ?? []} onChange={v => set({ conditions: v })}
        recommendedKeys={rec.conditions} columns={3} customPlaceholder="기타 조건 직접 입력" />
      <CheckboxGroupWithCustom label="걱정되는 위험요인" options={hazardOptions}
        value={d.hazards ?? []} onChange={v => set({ hazards: v })}
        recommendedKeys={hazardRec} columns={3} customPlaceholder="기타 위험요인 직접 입력" />
      <CheckboxGroupWithCustom label="지금 하고 있는 안전조치" options={SAFEGUARD_OPTIONS}
        value={d.safeguards ?? []} onChange={v => set({ safeguards: v })}
        recommendedKeys={rec.safeguards} columns={2} customPlaceholder="기타 조치 직접 입력" />
      <CheckboxGroupWithCustom label="쓰고 있는 보호구" options={ppeOptions}
        value={d.ppe ?? []} onChange={v => set({ ppe: v })}
        recommendedKeys={ppeRec} columns={3} customPlaceholder="기타 보호구 직접 입력" />
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────────────
 * STEP · 작업 선택 + 현장 상세 (한 화면 — 프리셋/서술/문서 업로드 → 작업 카드)
 * ──────────────────────────────────────────────────────────────────────────── */
export function TasksStep({ ksic, value, onChange, embedded, workers = 0, siteId,
  genTask, onGenTaskChange, onGenerate, busy, ctaLabel, footnote }: {
  ksic: string
  value: SetupTaskIn[]
  onChange: (next: SetupTaskIn[]) => void
  /** 홈 '작업 추가' 재사용 모드 — 헤더·라디오·CTA 없이 목록+상세만(버튼은 부모). */
  embedded?: boolean
  /** 상시 근로자 수 — 상세 시드의 인원 버킷 프리필. */
  workers?: number
  /** 사업장 문맥 — 있으면 보유 보호구(대장)·MSDS 화학물질을 상세 추천·시드에 주입. */
  siteId?: string | null
  /** 온보딩 무료 생성 대상 작업 1개 선택(라디오). 홈 재사용 시 미전달. */
  genTask?: string | null
  onGenTaskChange?: (name: string | null) => void
  /** 온보딩 — 이 화면에서 바로 생성(3·4단계 통합). */
  onGenerate?: () => void
  busy?: boolean
  ctaLabel?: string
  footnote?: string
}) {
  const presets = useMemo(() => presetsFor(ksic), [ksic])
  const [mode, setMode] = useState<'preset' | 'text' | 'file'>('preset')
  const [freeText, setFreeText] = useState('')
  const [parsing, setParsing] = useState(false)
  const [customName, setCustomName] = useState('')
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const fileRef = useRef<HTMLInputElement>(null)
  const seeded = useRef(false)

  // 사업장 보유 보호구(대장)·MSDS 화학물질 — 상세 옵션·추천·시드에 주입할 문맥.
  const sitePpeQ = useQuery({
    queryKey: ['tasksstep-site-ppe', siteId],
    queryFn: () => sitesApi.listEquipment(siteId!, { category: 'ppe' }),
    enabled: !!siteId, staleTime: 60_000,
  })
  const siteHazQ = useQuery({
    queryKey: ['tasksstep-site-hazards', siteId],
    queryFn: () => sitesApi.listHazards(siteId!),
    enabled: !!siteId, staleTime: 60_000,
  })
  const sitePpe = useMemo(() =>
    [...new Set((sitePpeQ.data ?? []).map(e => (e.name || '').trim()).filter(Boolean))].slice(0, 12),
    [sitePpeQ.data])
  const siteChemHazards = useMemo(() =>
    [...new Set((siteHazQ.data ?? [])
      .filter(h => h.hazard_type === 'chemical' && (h.name || '').trim())
      .map(h => `화학물질(${h.name.trim()}) 취급`))].slice(0, 10),
    [siteHazQ.data])
  const siteCtx = useMemo(() => ({ ppe: sitePpe, hazards: siteChemHazards }),
    [sitePpe, siteChemHazards])

  // 상세 입력은 필수 — 목록에 확정된 모든 작업 카드를 자동으로 펼치고 추천 시드를
  // 채운다(2026-08-19 지시: 접힌 상세를 확인하지 않은 채 생성으로 직행하는 문제).
  // autoOpened 로 '한 번 펼쳐준' 작업을 기억해, 사용자가 손으로 접은 카드를
  // 값이 바뀔 때마다 되펼치지 않는다.
  const autoOpened = useRef<Set<string>>(new Set())
  useEffect(() => {
    const fresh = value.filter(t => !autoOpened.current.has(t.name))
    if (!fresh.length) return
    fresh.forEach(t => autoOpened.current.add(t.name))
    setOpen(o => { const n = { ...o }; fresh.forEach(t => { n[t.name] = true }); return n })
    if (value.some(t => !t.detail)) {
      onChange(value.map(t => t.detail ? t : { ...t, detail: seedTaskDetail(t, workers, ksic, siteCtx) }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  // 온보딩 최초 진입 자동 체크는 **위험서열 1위 프리셋 1개만**(2026-08-09 사장님 지시).
  // 여러 개를 미리 체크하면 정신없고 자기 작업이 뭔지 인지하기 어렵다 — 이 단계는
  // "지금 만들 작업 1개"에 집중하고, 나머지는 가입 후 홈 '작업 추가'에서 등록한다.
  // ⚠️ embedded(홈 '작업 추가')에서는 자동 체크 금지 — 이미 있는 작업까지 몰래 제출되면
  //    의도치 않은 문서 재생성(쿼터 낭비)로 이어진다(실측). 홈은 사용자가 고른 것만.
  useEffect(() => {
    if (embedded || seeded.current || value.length > 0) return
    seeded.current = true
    const top = [...presets.filter(p => p.defaultOn)]
      .sort((a, b) => workTypeRiskRank(a.work_type) - workTypeRiskRank(b.work_type))[0]
    if (top) {
      onChange([{ name: top.name, category: top.category, work_type: top.work_type, hazard_hints: top.hints ?? [] }])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presets, embedded])

  // 무료 생성(라디오) 작업은 상세를 자동으로 펼치고 추천 시드 — 그 작업의 서류가
  // 지금 만들어지므로 상세 입력의 가치가 가장 큰 지점이다.
  useEffect(() => {
    if (embedded || !genTask) return
    const t = value.find(x => x.name === genTask)
    if (!t) return
    setOpen(o => (o[genTask] ? o : { ...o, [genTask]: true }))
    if (!t.detail) {
      onChange(value.map(x => x.name === genTask ? { ...x, detail: seedTaskDetail(x, workers, ksic, siteCtx) } : x))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genTask, value.length, embedded])

  const has = (name: string) => value.some(t => t.name === name)

  function toggle(name: string) {
    if (has(name)) { onChange(value.filter(t => t.name !== name)); return }
    if (value.length >= MAX_TASKS) { toast.error(`작업은 ${MAX_TASKS}개까지 등록할 수 있어요. 핵심 작업부터 골라 주세요.`); return }
    const p = presets.find(x => x.name === name)
    onChange([...value, p
      ? { name: p.name, category: p.category, work_type: p.work_type, hazard_hints: p.hints ?? [] }
      : { name, category: 'routine' }])
  }

  function addMany(items: SetupTaskIn[]) {
    const fresh = items.filter(t => !has(t.name))
    if (!fresh.length) { toast.info('이미 목록에 있는 작업이에요.'); return }
    const room = MAX_TASKS - value.length
    onChange([...value, ...fresh.slice(0, room)])
    toast.success(`작업 ${Math.min(fresh.length, room)}개를 목록에 추가했어요.`)
  }

  /** 이름 편집 — 라디오·펼침 상태도 새 이름으로 이관. 중복 이름은 거부. */
  function rename(oldName: string, newName: string) {
    const nm = newName.trim()
    if (!nm || nm === oldName) return
    if (has(nm)) { toast.error('같은 이름의 작업이 이미 있어요.'); return }
    onChange(value.map(t => t.name === oldName ? { ...t, name: nm } : t))
    setOpen(o => { const { [oldName]: was, ...rest } = o; return { ...rest, [nm]: !!was } })
    if (genTask === oldName) onGenTaskChange?.(nm)
  }

  function toggleOpen(t: SetupTaskIn) {
    const next = !open[t.name]
    setOpen(o => ({ ...o, [t.name]: next }))
    if (next && !t.detail) {
      onChange(value.map(x => x.name === t.name ? { ...x, detail: seedTaskDetail(x, workers, ksic, siteCtx) } : x))
    }
  }

  async function runParse(text: string) {
    setParsing(true)
    try {
      const tasks = await onboardingApi.parseTasks(text)
      addMany(tasks.map(t => ({ name: t.name, category: t.category, work_type: t.work_type ?? undefined })))
    } catch {
      toast.error('작업을 찾지 못했어요. 빠른 선택에서 골라 주세요.')
      setMode('preset')
    } finally { setParsing(false) }
  }

  async function onFile(f: File | null) {
    if (!f) return
    setParsing(true)
    try {
      const ext = await extractFileText(f)
      if (!ext.work_description?.trim()) throw new Error('empty')
      await runParse(ext.work_description.slice(0, 6000))
    } catch {
      toast.error('문서에서 텍스트를 읽지 못했어요. 빠른 선택에서 골라 주세요.')
      setMode('preset')
      setParsing(false)
    }
  }

  const routine = presets.filter(p => p.category === 'routine')
  const nonroutine = presets.filter(p => p.category === 'nonroutine')

  return (
    <div className="space-y-4">
      {!embedded && (
        <div className="space-y-1.5">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-brand-primary/10 px-2.5 py-1 text-[11px] font-semibold text-brand-primary">
            <FiGrid size={11} /> 우리 사업장 작업 등록
          </div>
          <h2 className="text-[20px] font-bold text-ink leading-snug">지금 가장 자주 하는 작업 1개를 골라 주세요</h2>
          <p className="text-[13px] text-ink-muted">
            고른 작업의 <b className="text-ink">위험성평가·JSA·작업허가서·법규점검</b> 서류를 <b className="text-ink">무료</b>로 만들어 드려요.
            지금은 <b className="text-ink">작업 1개에만 집중</b>하는 게 가장 정확합니다 —
            다른 작업들은 <b className="text-ink">가입 후 홈 화면의 '작업 추가'</b>에서 언제든 등록할 수 있어요.
          </p>
        </div>
      )}

      {/* 입력 모드 탭 */}
      <div className="flex gap-1.5">
        {([['preset', '빠른 선택', FiGrid], ['text', '글로 설명', FiEdit3], ['file', '문서 올리기', FiUploadCloud]] as const).map(([m, label, Icon]) => (
          <button key={m} type="button" onClick={() => setMode(m)}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[12px] font-semibold transition ${
              mode === m ? 'border-brand-primary text-brand-primary bg-brand-primary/10'
                         : 'border-surface-border text-ink-muted hover:text-ink'}`}>
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>

      {mode === 'preset' && (
        <div className="space-y-3">
          <div>
            <div className="text-[12px] font-bold text-ink-muted mb-1.5">매일·자주 하는 작업</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {routine.map(p => {
                const on = has(p.name)
                return (
                  <button key={p.name} type="button" onClick={() => toggle(p.name)}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition ${
                      on ? 'border-brand-primary bg-brand-primary/[0.07]' : 'border-surface-border hover:border-brand-primary/40'}`}>
                    <span className={`grid h-[18px] w-[18px] shrink-0 place-items-center rounded border ${
                      on ? 'border-brand-primary bg-brand-primary text-white' : 'border-surface-border'}`}>
                      {on && <FiCheck size={12} />}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[13px] font-semibold text-ink truncate">{p.name}</span>
                      {p.hints && p.hints.length > 0 && (
                        <span className="block text-[10.5px] text-ink-faint truncate">주의: {p.hints.join(' · ')}</span>
                      )}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
          {nonroutine.length > 0 && (
            <div>
              <div className="text-[12px] font-bold text-ink-muted mb-1.5">가끔 하는 작업 (정비·청소 등) <span className="font-normal text-ink-faint">— 사고가 잦은 구간이라 꼭 확인하세요</span></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {nonroutine.map(p => {
                  const on = has(p.name)
                  return (
                    <button key={p.name} type="button" onClick={() => toggle(p.name)}
                      className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition ${
                        on ? 'border-amber-500 bg-amber-500/[0.07]' : 'border-surface-border hover:border-amber-500/40'}`}>
                      <span className={`grid h-[18px] w-[18px] shrink-0 place-items-center rounded border ${
                        on ? 'border-amber-500 bg-amber-500 text-white' : 'border-surface-border'}`}>
                        {on && <FiCheck size={12} />}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[13px] font-semibold text-ink truncate">{p.name}</span>
                        {p.hints && p.hints.length > 0 && (
                          <span className="block text-[10.5px] text-ink-faint truncate">주의: {p.hints.join(' · ')}</span>
                        )}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}
          {/* 직접 추가 */}
          <div className="flex gap-1.5">
            <input value={customName} onChange={e => setCustomName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && customName.trim()) { toggle(customName.trim()); setCustomName('') } }}
              placeholder="목록에 없는 작업 직접 추가 (예: 도금조 관리)"
              className="flex-1 rounded-lg border border-surface-border bg-surface px-3 py-2 text-[13px] text-ink focus:outline-none focus:ring-2 focus:ring-brand-primary/40" />
            <Button variant="secondary" size="sm" disabled={!customName.trim()}
              onClick={() => { toggle(customName.trim()); setCustomName('') }} leftIcon={<FiPlus size={13} />}>추가</Button>
          </div>
        </div>
      )}

      {mode === 'text' && (
        <div className="space-y-2">
          <textarea value={freeText} onChange={e => setFreeText(e.target.value)} rows={4}
            placeholder={'우리 사업장이 하는 일을 편하게 적어 주세요.\n예) 프레스로 부품을 찍고 지게차로 나릅니다. 한 달에 한 번 금형을 교체해요.'}
            className="w-full rounded-xl border border-surface-border bg-surface px-3.5 py-2.5 text-[13px] text-ink leading-relaxed focus:outline-none focus:ring-2 focus:ring-brand-primary/40" />
          <Button variant="secondary" size="sm" disabled={parsing || freeText.trim().length < 5}
            onClick={() => runParse(freeText.trim())}
            leftIcon={parsing ? <Spinner size="sm" /> : <FiZap size={13} />}>
            {parsing ? '작업을 찾는 중…' : 'AI로 작업 찾기'}
          </Button>
        </div>
      )}

      {mode === 'file' && (
        <div className="space-y-2">
          <button type="button" onClick={() => fileRef.current?.click()} disabled={parsing}
            className="w-full rounded-xl border-2 border-dashed border-surface-border hover:border-brand-primary/50 px-4 py-6 text-center transition">
            {parsing ? (
              <span className="inline-flex items-center gap-2 text-[13px] text-ink-muted"><Spinner size="sm" /> 문서에서 작업을 찾는 중…</span>
            ) : (
              <>
                <FiUploadCloud className="mx-auto text-ink-faint" size={22} />
                <div className="mt-1.5 text-[13px] font-semibold text-ink">기존 서류 올리기 (PDF·워드·텍스트)</div>
                <div className="text-[11.5px] text-ink-faint">기존 위험성평가·작업표준·계약서에서 작업 목록을 자동으로 찾아 드려요</div>
              </>
            )}
          </button>
          <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.hwp,.hwpx" className="hidden"
            onChange={e => onFile(e.target.files?.[0] ?? null)} />
        </div>
      )}

      {/* 확정 목록 — 작업 카드(이름 편집·상시/비일상·라디오·인라인 상세). 모든 입력 모드의 수렴점 */}
      <div className="rounded-xl border border-surface-border bg-surface-elevated/25 p-3 space-y-1.5">
        <div className="flex items-center justify-between">
          <div className="text-[12px] font-bold text-ink">
            {embedded ? (
              <>선택한 작업 <span className="text-brand-primary">{value.length}</span>/{MAX_TASKS}</>
            ) : value.length <= 1 ? (
              <>선택한 작업 <span className="text-brand-primary">{value.length}</span>개 — 이 작업의 서류를 무료로 만들어요</>
            ) : (
              <>선택한 작업 <span className="text-brand-primary">{value.length}</span>개
                <span className="ml-1.5 font-semibold text-ink-muted">— 무료 서류를 만들 작업 <span className="text-brand-primary">1개</span>를 골라 주세요</span></>
            )}
          </div>
          {value.length > 0 && (
            <button type="button" onClick={() => { onChange([]); setOpen({}) }} className="text-[11px] text-ink-faint hover:text-ink">전체 지우기</button>
          )}
        </div>
        {value.length === 0 ? (
          <div className="text-[12px] text-ink-faint py-1.5">위에서 작업을 선택해 주세요.</div>
        ) : (
          <div className="space-y-1.5">
            {value.map(t => {
              const isGen = genTask === t.name
              const opened = !!open[t.name]
              return (
                <div key={t.name}>
                  <div className={`flex items-center gap-2 rounded-t-xl border px-2.5 py-1.5 ${opened ? '' : 'rounded-b-xl'} ${
                    isGen ? 'border-brand-primary bg-brand-primary/[0.06]'
                      : t.category === 'nonroutine' ? 'border-amber-400/50 bg-amber-500/[0.05]'
                      : 'border-surface-border'}`}>
                    {!embedded && onGenTaskChange && (
                      <input type="radio" name="gen-task" className="shrink-0 accent-[var(--brand-primary,#2563eb)]"
                        checked={isGen} onChange={() => onGenTaskChange(t.name)} title="무료 서류를 만들 작업" />
                    )}
                    <TaskNameInput name={t.name} onCommit={nm => rename(t.name, nm)} />
                    {isGen && <span className="shrink-0 rounded bg-brand-primary/12 px-1.5 py-0.5 text-[9.5px] font-bold text-brand-primary">무료 생성</span>}
                    <button type="button" title={t.category === 'nonroutine' ? '비일상 → 상시로 변경' : '상시 → 비일상으로 변경'}
                      onClick={() => onChange(value.map(x => x.name === t.name
                        ? { ...x, category: x.category === 'nonroutine' ? 'routine' : 'nonroutine' } : x))}
                      className="shrink-0 rounded bg-surface-elevated/60 px-1.5 py-0.5 text-[9.5px] text-ink-faint hover:text-ink">
                      {t.category === 'nonroutine' ? '비일상' : '상시'}
                    </button>
                    <button type="button" onClick={() => toggleOpen(t)}
                      className={`shrink-0 inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10.5px] font-semibold ${
                        opened ? 'text-brand-primary' : t.detail ? 'text-emerald-600' : 'text-ink-muted hover:text-ink'}`}>
                      {t.detail && !opened ? '상세 ✓' : '상세 입력(1분)'}
                      {opened ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />}
                    </button>
                    <button type="button" onClick={() => { onChange(value.filter(x => x.name !== t.name)); setOpen(o => ({ ...o, [t.name]: false })) }}
                      className="shrink-0 text-ink-faint hover:text-red-500"><FiX size={13} /></button>
                  </div>
                  {opened && (
                    <TaskDetailFields task={t} ksic={ksic} sitePpe={sitePpe} siteChemHazards={siteChemHazards}
                      onChange={d => onChange(value.map(x => x.name === t.name ? { ...x, detail: d } : x))}
                      onRename={nm => rename(t.name, nm)} />
                  )}
                </div>
              )
            })}
          </div>
        )}
        {!embedded && onGenTaskChange && value.length > 1 && (
          <p className="text-[10.5px] text-ink-faint leading-relaxed">
            나머지 {value.length - 1}개 작업도 함께 등록됩니다 — 서류는 사업장 홈 작업 보드에서 이어서 만들 수 있어요.
            각 작업의 <b className="text-ink-muted">상세 입력(1분)</b>을 채우면 그 작업 서류가 훨씬 정확해집니다.
          </p>
        )}
      </div>

      {!embedded && onGenerate && (
        <div className="pt-1 space-y-2">
          <Button variant="primary" className="w-full !py-3 text-[15px]" disabled={busy || value.length === 0}
            onClick={onGenerate} rightIcon={busy ? undefined : <FiZap size={18} />}>
            {busy ? '시작하는 중…' : (ctaLabel ?? '무료로 기준 서류 4종 만들기')}
          </Button>
          <p className="text-center text-[11px] text-ink-faint">
            {footnote ?? '선택한 작업 1개의 위험성평가 · JSA · 작업허가서 · 법규 준수 점검 — 전액 무료, 사용량 미차감'}
          </p>
        </div>
      )}
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────────────
 * STEP · 생성 진행 (잡 폴링 표시)
 * ──────────────────────────────────────────────────────────────────────────── */
export function GeneratingStep({ status }: { status: SetupJobStatus | null }) {
  const steps = status?.steps ?? []
  const doneCount = steps.filter(s => s.status === 'done').length
  return (
    <div className="space-y-4 py-2">
      <div className="text-center space-y-1.5">
        <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-brand-primary/10">
          <FiLoader className="animate-spin text-brand-primary" size={22} />
        </div>
        <h2 className="text-[19px] font-bold text-ink">서류를 만들고 있어요 ({doneCount}/{steps.length || 4})</h2>
        <p className="text-[12.5px] text-ink-muted">270만 건 사고데이터와 법령을 대조해 우리 작업 기준으로 작성 중입니다.<br/>
          <b className="text-ink">이 창을 닫아도 생성은 계속됩니다</b> — 완성되면 사업장 홈 서류함에 저장돼요.</p>
      </div>
      <div className="space-y-1.5">
        {steps.map(s => (
          <div key={s.key ?? s.kind} className={`flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5 ${
            s.status === 'done' ? 'border-emerald-400/40 bg-emerald-500/[0.05]'
              : s.status === 'error' ? 'border-red-400/40 bg-red-500/[0.04]'
              : s.status === 'running' ? 'border-brand-primary/40 bg-brand-primary/[0.05]'
              : 'border-surface-border'}`}>
            <span className="shrink-0">
              {s.status === 'done' ? <FiCheck className="text-emerald-500" size={16} />
                : s.status === 'error' ? <FiAlertTriangle className="text-red-500" size={15} />
                : s.status === 'running' ? <Spinner size="sm" />
                : <span className="block h-2 w-2 rounded-full bg-surface-border" />}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-semibold text-ink">{s.label}</span>
              {s.status === 'running' && s.note && <span className="block text-[11px] text-ink-muted truncate">{s.note}</span>}
              {s.status === 'error' && <span className="block text-[11px] text-red-500 truncate">{s.note || '생성 실패 — 서류함에서 다시 만들 수 있어요'}</span>}
              {s.status === 'skipped' && <span className="block text-[11px] text-ink-faint truncate">{s.note || '대상 아님'}</span>}
            </span>
          </div>
        ))}
        {steps.length === 0 && (
          <div className="flex items-center justify-center gap-2 py-6 text-[13px] text-ink-muted"><Spinner size="sm" /> 준비 중…</div>
        )}
      </div>
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────────────
 * STEP · 완료
 * ──────────────────────────────────────────────────────────────────────────── */
const RA_LEVEL_STYLE: Record<string, string> = {
  '매우높음': 'bg-red-500/15 text-red-600 dark:text-red-300 border-red-400/40',
  '높음': 'bg-orange-500/15 text-orange-600 dark:text-orange-300 border-orange-400/40',
  '보통': 'bg-amber-500/15 text-amber-600 dark:text-amber-300 border-amber-400/40',
  '낮음': 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border-emerald-400/40',
  '매우낮음': 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border-emerald-400/40',
}

/** 생성 완료 직후 위험성평가 항목 확인·선택 팝업(2026-08-09 사장님 지시).
 *
 * 일반 생성기와 동일한 방식 — AI가 여러 항목을 뽑아놓으면 **사용자가 고른 항목만**
 * 평가표에 남는다. 온보딩 잡이 서버에서 전량 저장하므로, 여기서 선택을 받아
 * saveItems(PUT — 페이로드에 없는 행은 삭제)로 확정한다. 전체 유지면 무변경. */
export function RaReviewModal({ siteId, raId, onClose }: {
  siteId: string; raId: string; onClose: () => void
}) {
  const [items, setItems] = useState<RAItem[] | null>(null)
  const [checked, setChecked] = useState<Record<string, boolean>>({})
  const [saving, setSaving] = useState(false)
  const [loadFail, setLoadFail] = useState(false)

  useEffect(() => {
    let alive = true
    riskAssessmentApi.get(siteId, raId)
      .then(ra => {
        if (!alive) return
        const its = (ra.items ?? []).filter(i => i.id)
        setItems(its)
        setChecked(Object.fromEntries(its.map(i => [i.id as string, true])))
      })
      .catch(() => { if (alive) setLoadFail(true) })
    return () => { alive = false }
  }, [siteId, raId])

  const selCount = items ? items.filter(i => checked[i.id as string]).length : 0

  const confirm = async () => {
    if (!items || items.length === 0) { onClose(); return }
    const sel = items.filter(i => checked[i.id as string])
    if (sel.length === 0) { toast.error('최소 1개 항목은 남겨 주세요.'); return }
    if (sel.length === items.length) { onClose(); return }  // 전체 유지 = 서버 무변경
    setSaving(true)
    try {
      await riskAssessmentApi.saveItems(siteId, raId, sel.map((i, idx) => ({ ...i, seq: idx + 1 })))
      toast.success(`선택한 ${sel.length}개 항목만 위험성평가표에 저장했어요.`)
      onClose()
    } catch {
      toast.error('저장하지 못했어요. 잠시 후 서류함에서 다시 수정할 수 있습니다.')
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-3xl rounded-2xl bg-surface-card border border-surface-border shadow-2xl flex flex-col max-h-[86vh]">
        <div className="p-5 pb-3 space-y-1">
          <h3 className="text-[17px] font-bold text-ink">위험성평가 내용을 확인해 주세요</h3>
          <p className="text-[12.5px] text-ink-muted">
            위험성평가는 최대한 많은 위험요인을 발굴하는 것이 목적입니다. <b className="text-ink">현장에 해당하는 항목만</b> 남겨
            주세요(기본 전체 선택) — 체크를 해제한 항목은 평가표에서 빠집니다. 저장 후에도 서류함에서 언제든 수정할 수 있어요.
          </p>
          {items && items.length > 0 && (
            <div className="flex items-center justify-between pt-1">
              <span className="text-xs font-bold text-ink-muted">⚠ 도출된 위험요인 {items.length}개 · 선택 {selCount}개</span>
              <span className="flex gap-1.5 text-[11px]">
                <button type="button" className="text-brand-primary"
                  onClick={() => setChecked(Object.fromEntries(items.map(i => [i.id as string, true])))}>전체선택</button>
                <span className="text-ink-faint">·</span>
                <button type="button" className="text-ink-muted"
                  onClick={() => setChecked(Object.fromEntries(items.map(i => [i.id as string, false])))}>전체해제</button>
              </span>
            </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto px-5 space-y-3">
          {loadFail ? (
            <p className="py-6 text-center text-[12.5px] text-ink-muted">내용을 불러오지 못했어요 — 서류함에서 직접 확인해 주세요.</p>
          ) : !items ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : (() => {
            const groups: [string, RAItem[]][] = []
            for (const it of items) {
              const key = it.unit_task || '기타'
              const g = groups.find(x => x[0] === key)
              if (g) g[1].push(it); else groups.push([key, [it]])
            }
            return groups.map(([unit, group]) => (
              <div key={unit}>
                <div className="text-[11px] font-bold text-ink-muted bg-surface-elevated/40 rounded px-2 py-1 mb-1">🔧 {unit} ({group.length})</div>
                <div className="space-y-1.5">
                  {group.map(it => {
                    const id = it.id as string
                    const on = !!checked[id]
                    const acceptable = it.acceptable === true
                    return (
                      <label key={id} className={`block rounded-lg border p-2 cursor-pointer transition ${on ? 'border-brand-primary/50 bg-brand-primary/5' : 'border-surface-elevated/40 opacity-70'}`}>
                        <div className="flex items-start gap-2">
                          <input type="checkbox" checked={on}
                            onChange={e => setChecked(c => ({ ...c, [id]: e.target.checked }))}
                            className="mt-1 accent-brand-primary" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {it.hazard_category && <span className="text-[10px] px-1 rounded bg-surface-elevated/70 text-ink-muted">{it.hazard_category}</span>}
                              <span className="text-sm font-medium text-ink">{it.hazard}</span>
                              {it.risk_level && <span className={`text-[10px] px-1.5 rounded border ${RA_LEVEL_STYLE[it.risk_level] || ''}`}>현재 {it.risk_level}{it.risk_score ? ` (${it.risk_score})` : ''}</span>}
                              {!acceptable && it.residual_level && (
                                <span className="text-[10px] text-ink-muted">→ 개선후{' '}
                                  <span className={`px-1 rounded border ${RA_LEVEL_STYLE[it.residual_level] || ''}`}>{it.residual_level}{it.residual_score ? ` (${it.residual_score})` : ''}</span>
                                </span>
                              )}
                            </div>
                            {it.current_controls && <div className="text-[11px] text-ink-faint mt-0.5">현재 안전조치: {it.current_controls}</div>}
                            {it.cause && <div className="text-[11px] text-ink-muted mt-0.5">원인: {it.cause}{(it.frequency && it.severity && it.risk_score) ? ` · 빈도${it.frequency}×강도${it.severity} = ${it.risk_score}점` : ''}</div>}
                            {acceptable ? (
                              <div className="text-[11px] text-emerald-600 dark:text-emerald-300 mt-1">✓ 허용 가능 — 현 수준 유지 (추가 개선대책 불요)</div>
                            ) : (it.reduction_measures ?? []).length > 0 && (
                              <div className="mt-1 space-y-0.5">
                                {(it.reduction_measures ?? []).map((m, mi) => (
                                  <div key={mi} className="text-[11px] flex gap-1.5">
                                    <span className="font-semibold text-brand-primary shrink-0">[{m.tier ? (TIER_LABEL[m.tier] ?? m.tier) : '대책'}]</span>
                                    <span className="text-ink">{m.measure}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {it.legal_basis?.article_no && (
                              <div className="text-[11px] text-brand-primary mt-0.5">⚖ {it.legal_basis.article_no} {it.legal_basis.article_title ?? ''}</div>
                            )}
                          </div>
                        </div>
                      </label>
                    )
                  })}
                </div>
              </div>
            ))
          })()}
        </div>
        <div className="p-5 pt-3 flex items-center gap-2">
          <span className="text-[12px] text-ink-muted">선택 {selCount} / {items?.length ?? 0}개</span>
          <div className="flex-1" />
          <Button variant="ghost" onClick={onClose} disabled={saving}>전체 유지</Button>
          <Button onClick={confirm} disabled={saving || !items}>
            {saving ? '저장 중…' : '선택한 항목만 저장'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export function DoneStep({ status, taskCount, onGoHome, onGoTour, busy }: {
  status: SetupJobStatus | null
  taskCount: number
  onGoHome: () => void
  onGoTour: () => void
  busy: boolean
}) {
  const created = status?.created ?? []
  const failed = Object.keys(status?.errors ?? {}).filter(k => k !== '_')
  return (
    <div className="space-y-5 py-2 text-center">
      <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-emerald-500/12">
        <FiCheck className="text-emerald-500" size={26} />
      </div>
      <div className="space-y-1.5">
        <h2 className="text-[21px] font-bold text-ink">서류함에 {created.length}건이 저장됐어요</h2>
        <p className="text-[13px] text-ink-muted">
          작업 {taskCount}개가 등록됐어요.{created[0]?.task_name && (
            <> <b className="text-ink">{created[0].task_name}</b>의 기준 서류가 완성됐고,</>
          )} 이제 매일 아침 <b className="text-ink">아침 안전 브리핑</b>만 만들면 됩니다.
          {taskCount > 1 && <><br/>나머지 작업의 서류는 <b className="text-ink">작업 보드</b>에서 이어서 만들 수 있어요.</>}
        </p>
      </div>
      <div className="mx-auto max-w-sm space-y-1.5 text-left">
        {created.map(c => (
          <div key={`${c.kind}-${c.ref_id}`} className="flex items-center gap-2 rounded-lg border border-emerald-400/30 bg-emerald-500/[0.05] px-3 py-2">
            <FiFileText className="shrink-0 text-emerald-600" size={14} />
            <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium text-ink">{(c.title || c.kind).split('\n')[0]}</span>
            <FiCheck className="shrink-0 text-emerald-500" size={13} />
          </div>
        ))}
        {failed.length > 0 && (
          <div className="rounded-lg border border-amber-400/40 bg-amber-500/[0.06] px-3 py-2 text-[11.5px] text-amber-700 dark:text-amber-300">
            {failed.length}건은 만들지 못했어요 — 사업장 홈에서 언제든 다시 만들 수 있습니다.
          </div>
        )}
      </div>
      <div className="space-y-2">
        <Button variant="primary" className="w-full !py-3 text-[15px]" disabled={busy} onClick={onGoHome}
          rightIcon={<FiArrowRight size={18} />}>내 사업장 홈 보기</Button>
        <button type="button" onClick={onGoTour} disabled={busy}
          className="text-[12.5px] font-medium text-ink-muted hover:text-ink underline underline-offset-2">
          사용법 둘러보기(데모 투어)부터 볼래요
        </button>
      </div>
      <p className="text-[11px] text-ink-faint leading-relaxed">
        원청(발주처)에 제출할 서류라면 — 사업장 홈의 <b className="text-ink-muted">‘원청 연결’</b>에서
        요청을 받고 바로 공유할 수 있어요.
      </p>
    </div>
  )
}
